import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Users, Home, UserPlus, Trash2, Crown, Mail, 
  TrendingUp, FileText, Lightbulb, Calendar, Loader2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Tooltip from '../components/Tooltip';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const TeamDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [teamData, setTeamData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [teamSessions, setTeamSessions] = useState([]);
  const [teamEvaluations, setTeamEvaluations] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [removingMember, setRemovingMember] = useState(null);

  useEffect(() => {
    const initTeamDashboard = async () => {
      if (!user) {
        navigate('/auth');
        return;
      }

      // Check if user has team, if not redirect to create
      if (!user.team_id) {
        // Try to fetch fresh user data in case it was just created
        try {
          const profileRes = await axios.get(`${BACKEND_URL}/api/profile`, {
            withCredentials: true
          });
          
          if (!profileRes.data.team_id) {
            navigate('/teams/create');
            return;
          }
        } catch (error) {
          console.error('Error fetching profile:', error);
          navigate('/teams/create');
          return;
        }
      }

      fetchTeamData();
      fetchAnalytics();
      fetchTeamSessions();
      fetchTeamEvaluations();
    };

    initTeamDashboard();
  }, [user, navigate]);

  const fetchTeamData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/teams/${user.team_id}`, {
        withCredentials: true
      });
      setTeamData(response.data);
    } catch (error) {
      console.error('Error fetching team:', error);
      toast({
        title: "Error",
        description: "Failed to load team data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/teams/${user.team_id}/analytics`, {
        withCredentials: true
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchTeamSessions = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/teams/${user.team_id}/sessions`, {
        withCredentials: true
      });
      setTeamSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Error fetching team sessions:', error);
    }
  };

  const fetchTeamEvaluations = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/teams/${user.team_id}/evaluations`, {
        withCredentials: true
      });
      setTeamEvaluations(response.data.evaluations || []);
    } catch (error) {
      console.error('Error fetching team evaluations:', error);
    }
  };

  const handleInviteMember = async (e) => {
    e.preventDefault();
    
    if (!inviteEmail) {
      toast({
        title: "Error",
        description: "Please enter an email address",
        variant: "destructive"
      });
      return;
    }

    setInviting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/teams/${user.team_id}/invite`,
        {
          invitee_email: inviteEmail,
          invitee_name: inviteName || null
        },
        { withCredentials: true }
      );

      toast({
        title: "Success!",
        description: `Invitation sent to ${inviteEmail}`
      });

      setInviteEmail('');
      setInviteName('');
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send invitation",
        variant: "destructive"
      });
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) {
      return;
    }

    setRemovingMember(memberId);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/teams/${user.team_id}/members/${memberId}`,
        { withCredentials: true }
      );

      toast({
        title: "Success!",
        description: "Member removed from team"
      });

      fetchTeamData();
      fetchAnalytics();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to remove member",
        variant: "destructive"
      });
    } finally {
      setRemovingMember(null);
    }
  };

  const isOwner = teamData && teamData.owner_id === user.id;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="border-b bg-white/90 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-purple-600" />
            <div>
              <h1 className="text-2xl font-bold">{teamData?.name || 'Team Dashboard'}</h1>
              <div className="flex items-center gap-2">
                <p className="text-sm text-slate-600">{teamData?.member_count || 0} members</p>
                {teamData?.if_team_id && (
                  <>
                    <span className="text-slate-300">•</span>
                    <code className="text-xs font-mono bg-purple-100 text-purple-700 px-2 py-1 rounded">
                      {teamData.if_team_id}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(teamData.if_team_id);
                        toast({
                          title: "Copied!",
                          description: "Team ID copied to clipboard"
                        });
                      }}
                      className="text-xs text-purple-600 hover:text-purple-800"
                    >
                      Copy
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
          <Tooltip content="Return to dashboard">
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              <Home className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </Tooltip>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12 max-w-7xl">
        {/* Analytics Cards */}
        {analytics && (
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600">Total Members</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-600">{analytics.total_members}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600">Team Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">{analytics.total_sessions}</div>
                <p className="text-xs text-slate-500 mt-1">{analytics.completed_sessions} completed</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600">Evaluations</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-emerald-600">{analytics.total_evaluations}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600">Avg OI Score</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-orange-600">{analytics.avg_oi_score}</div>
                <p className="text-xs text-slate-500 mt-1">Team average</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'overview'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('sessions')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'sessions'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Shared Sessions ({teamSessions.length})
          </button>
          <button
            onClick={() => setActiveTab('evaluations')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'evaluations'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Evaluations ({teamEvaluations.length})
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Team ID Card */}
            <Card className="md:col-span-2 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-900">
                  Team ID
                </CardTitle>
                <CardDescription>
                  Share this code with your team members (format: #IFT-XXXX)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="flex-1 bg-white p-4 rounded-lg border-2 border-purple-300">
                    <code className="text-3xl font-mono font-bold text-purple-900">
                      {teamData?.if_team_id || 'Loading...'}
                    </code>
                  </div>
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(teamData?.if_team_id || '');
                      toast({
                        title: "Copied!",
                        description: "Team ID copied to clipboard"
                      });
                    }}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                  >
                    Copy ID
                  </Button>
                </div>
              </CardContent>
            </Card>


            {/* Team Members */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  Team Members
                </CardTitle>
                <CardDescription>
                  {teamData?.member_count}/{teamData?.max_members} members
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {teamData?.members?.map((member) => (
                    <div
                      key={member.user_id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div>
                        <div className="font-semibold text-slate-900">{member.name}</div>
                        <div className="text-sm text-slate-600">{member.email}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        {member.role === 'owner' ? (
                          <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                            <Crown className="w-3 h-3 mr-1" />
                            Owner
                          </Badge>
                        ) : (
                          <Badge variant="outline">Member</Badge>
                        )}
                        {isOwner && member.role !== 'owner' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveMember(member.user_id)}
                            disabled={removingMember === member.user_id}
                          >
                            {removingMember === member.user_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4 text-red-600" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Invite Members (Owner Only) */}
            {isOwner && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-purple-600" />
                    Invite Team Member
                  </CardTitle>
                  <CardDescription>
                    Send an invitation to join your team
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleInviteMember} className="space-y-4">
                    <div>
                      <label className="text-sm font-semibold text-slate-700">Email Address *</label>
                      <Input
                        type="email"
                        placeholder="colleague@example.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-slate-700">Name (Optional)</label>
                      <Input
                        type="text"
                        placeholder="John Doe"
                        value={inviteName}
                        onChange={(e) => setInviteName(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600"
                      disabled={inviting}
                    >
                      {inviting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4 mr-2" />
                          Send Invitation
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Member Activity */}
            {analytics && analytics.member_activity && (
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                    Member Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {analytics.member_activity.map((member) => (
                      <div
                        key={member.user_id}
                        className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-200"
                      >
                        <div className="font-semibold text-slate-900 mb-3">{member.name}</div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <div className="text-slate-600">Sessions</div>
                            <div className="text-2xl font-bold text-purple-600">{member.sessions}</div>
                          </div>
                          <div>
                            <div className="text-slate-600">Evaluations</div>
                            <div className="text-2xl font-bold text-pink-600">{member.evaluations}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="grid md:grid-cols-2 gap-6">
            {teamSessions.length === 0 ? (
              <Card className="md:col-span-2">
                <CardContent className="text-center py-12">
                  <Lightbulb className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600 mb-4">No shared sessions yet</p>
                  <Button
                    onClick={() => navigate('/session/new')}
                    className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600"
                  >
                    Create First Session
                  </Button>
                </CardContent>
              </Card>
            ) : (
              teamSessions.map((session) => (
                <Card key={session.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader>
                    <CardTitle className="text-lg">{session.title}</CardTitle>
                    <CardDescription>
                      by {session.creator_name || 'Unknown'} • {new Date(session.created_at).toLocaleDateString()}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="flex gap-2">
                        {session.completed && (
                          <Badge className="bg-green-100 text-green-800">Completed</Badge>
                        )}
                        {session.oi_scores && (
                          <Badge variant="outline">OI: {session.oi_scores.total_oi?.toFixed(1)}</Badge>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/session/${session.id}`)}
                      >
                        View
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Evaluations Tab */}
        {activeTab === 'evaluations' && (
          <div className="grid md:grid-cols-2 gap-6">
            {teamEvaluations.length === 0 ? (
              <Card className="md:col-span-2">
                <CardContent className="text-center py-12">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600 mb-4">No shared evaluations yet</p>
                  <Button
                    onClick={() => navigate('/evaluate')}
                    className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600"
                  >
                    Evaluate First Paper
                  </Button>
                </CardContent>
              </Card>
            ) : (
              teamEvaluations.map((evaluation) => (
                <Card key={evaluation.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg">{evaluation.paper_title}</CardTitle>
                    <CardDescription>
                      by {evaluation.creator_name || 'Unknown'} • {new Date(evaluation.created_at).toLocaleDateString()}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">OI Score:</span>
                        <span className="font-bold text-purple-600">{evaluation.oi_total?.toFixed(1)}/10</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Spark:</span>
                        <span className="font-bold text-pink-600">{evaluation.msa_spark_pct}%</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamDashboard;
